import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import { getAppVersion } from '@/services/versionService';

const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const LAST_NOTIFIED_VERSION_KEY = 'app.version.lastNotified';

export const useVersionUpdateNotification = (): void => {
  const { t } = useTranslation();
  const configuredVersion = import.meta.env.VITE_APP_VERSION?.trim() || null;
  const currentVersionRef = useRef<string | null>(configuredVersion);
  const lastNotifiedVersionRef = useRef<string | null>(null);
  const hasPromptedRef = useRef(false);
  const isPendingRef = useRef(false);
  const latestToastRef = useRef<{ dismiss: () => void } | null>(null);

  const pollInterval = useMemo(() => {
    const raw = Number(import.meta.env.VITE_APP_VERSION_POLL_INTERVAL_MS);
    if (Number.isFinite(raw) && raw >= 15_000) return raw;
    return DEFAULT_POLL_INTERVAL_MS;
  }, []);

  const readLastNotifiedVersion = (): string | null => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(LAST_NOTIFIED_VERSION_KEY);
    } catch (storageError) {
      logging.warn('version.storage_read_failed', { storageError });
      return null;
    }
  };

  const writeLastNotifiedVersion = (value: string | null): void => {
    if (typeof window === 'undefined') return;
    try {
      if (!value) {
        window.localStorage.removeItem(LAST_NOTIFIED_VERSION_KEY);
      } else {
        window.localStorage.setItem(LAST_NOTIFIED_VERSION_KEY, value);
      }
    } catch (storageError) {
      logging.warn('version.storage_write_failed', { storageError });
    }
  };

  const applyUpdate = (nextVersion: string) => {
    lastNotifiedVersionRef.current = nextVersion;
    writeLastNotifiedVersion(nextVersion);
    analytics.track('ui.app_update_apply_click', {
      currentVersion: currentVersionRef.current ?? 'unknown',
      targetVersion: nextVersion,
    });
    latestToastRef.current?.dismiss();
    window.location.reload();
  };

  const promptForUpdate = (nextVersion: string) => {
    if (hasPromptedRef.current) return;
    if (lastNotifiedVersionRef.current === nextVersion) return;

    lastNotifiedVersionRef.current = nextVersion;
    writeLastNotifiedVersion(nextVersion);
    hasPromptedRef.current = true;
    analytics.track('ui.app_update_available', {
      currentVersion: currentVersionRef.current ?? 'unknown',
      targetVersion: nextVersion,
    });
    latestToastRef.current = toast({
      title: t('updates.title'),
      description: t('updates.description'),
      duration: Number.POSITIVE_INFINITY,
      action: (
        <ToastAction
          altText={t('updates.applyActionAlt')}
          onClick={() => applyUpdate(nextVersion)}
        >
          {t('updates.applyAction')}
        </ToastAction>
      ),
    });
  };

  const checkForUpdates = async () => {
    if (isPendingRef.current) return;
    isPendingRef.current = true;
    try {
      const { version: latestVersion } = await getAppVersion();
      lastNotifiedVersionRef.current = readLastNotifiedVersion();

      logging.info(`version check: current=${currentVersionRef.current ?? 'unknown'} latest=${latestVersion ?? 'none'}`, {
        currentVersion: currentVersionRef.current,
        latestVersion,
        lastNotifiedVersion: lastNotifiedVersionRef.current,
        hasPrompted: hasPromptedRef.current,
      });

      if (!latestVersion) {
        return;
      }

      if (!currentVersionRef.current) {
        currentVersionRef.current = latestVersion;
        hasPromptedRef.current = false;
        return;
      }

      if (latestVersion === currentVersionRef.current) {
        if (lastNotifiedVersionRef.current) {
          lastNotifiedVersionRef.current = null;
          writeLastNotifiedVersion(null);
        }
        hasPromptedRef.current = false;
        return;
      }

      if (lastNotifiedVersionRef.current === latestVersion) {
        return;
      }

      promptForUpdate(latestVersion);
    } catch (error) {
      logging.warn('version.check_failed', { error });
    } finally {
      isPendingRef.current = false;
    }
  };

  useEffect(() => {
    if (import.meta.env.DEV) {
      return undefined;
    }

    // Initialize the last notified version from shared storage on mount.
    lastNotifiedVersionRef.current = readLastNotifiedVersion();
    if (lastNotifiedVersionRef.current && lastNotifiedVersionRef.current === currentVersionRef.current) {
      writeLastNotifiedVersion(null);
      lastNotifiedVersionRef.current = null;
    }

    void checkForUpdates();
    const intervalId = window.setInterval(() => {
      void checkForUpdates();
    }, pollInterval);

    return () => {
      window.clearInterval(intervalId);
      latestToastRef.current = null;
    };
  }, [pollInterval]);
};
